"use client";

import { Button, buttonVariants } from "@/components/atoms/buttons";
import InspirationProductCard from "@/components/molecules/inspiraion-product-card";
import { useProducts } from "@/hooks/useProduct";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn, extractKeyTextValue, getMedusaProductPrice, isSaleProduct } from "@/lib/utils";
import { ProductDocument } from "@/prismicio-types";
import { ShoppingCart, Loader2 } from "lucide-react";
import { useAddToCart } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import Link from "next/link";
import { useCart } from "@/hooks/useCart";
import { useRef, useState } from "react";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import InspirationProductMobile from "./inspiration-product-mobile";
import { getCartQuantityStep, normalizePieceQuantity, resolveProductConditioning } from "@/lib/product-units";

type InspirationProductsListProps = {
  products: ProductDocument[];
  locale: Locales;
};

const InspirationProductsList = ({ products, locale }: InspirationProductsListProps) => {
  const medusaIds = products.map((product) => extractKeyTextValue(product.data.medusaId));
  const { data: medusaProducts, isPending } = useProducts(locale, medusaIds, medusaIds.length > 0);
  const { cart } = useCart();

  // Store quantities for each product
  const quantitiesRef = useRef<Record<string, number>>({});

  const { addToCartAsync } = useAddToCart();
  const [isAddingAllToCart, setIsAddingAllToCart] = useState(false);

  const productsArray = Object.values(medusaProducts || {});

  // Function to update quantity for a specific product
  const updateProductQuantity = (medusaId: string, quantity: number) => {
    quantitiesRef.current[medusaId] = quantity;
  };

  const handleAddToCart = async () => {
    if (!medusaProducts || isAddingAllToCart) return;

    setIsAddingAllToCart(true);
    try {
      // Serialize writes to the same cart and await the real API result.
      // Stop on a refusal and report the incomplete pack; don't keep adding
      // more items after a failure. Already-added items are skipped on retry.
      for (const medusaProduct of productsArray.filter(
        (product) => product.status !== "draft" && (product.variants?.length ?? 0) <= 1
      )) {
        const medusaId = medusaProduct.id;
        if (!medusaProduct?.variants?.[0]?.id) {
          console.warn(`No variant found for product ${medusaId}`);
          continue;
        }

        if (cart?.items?.some((item) => item.variant_id === medusaProduct.variants?.[0]?.id)) continue;

        const conditioning = resolveProductConditioning(
          medusaProduct.metadata?.conditioning,
          medusaProduct.metadata?.conditioning_bac
        );
        const quantity = normalizePieceQuantity(
          quantitiesRef.current[medusaId] || getCartQuantityStep(conditioning),
          conditioning
        );
        await addToCartAsync(medusaProduct.variants[0].id, quantity);
      }

      toast.success({
        title: __("All products added to cart!", locale),
      });
    } catch (error) {
      console.error("Error adding products to cart:", error);
      toast.error({
        title: __("Failed to add some products to cart", locale),
      });
    } finally {
      setIsAddingAllToCart(false);
    }
  };

  const isProductsInCart = productsArray
    .filter((product) => product.status !== "draft")
    .every((product) => {
      return cart?.items && cart.items.some((item) => item.product_handle === product?.handle);
    });

  const isAllProductsOutOfStock = productsArray.every((product) => product.status === "draft");

  return (
    <>
      <ul className={cn("max-sm:hidden", products.length > 4 && "pr-5")}>
        {products.map((product) => {
          const medusaId = extractKeyTextValue(product.data.medusaId);
          const medusaProduct = medusaProducts?.[medusaId];

          return (
            <li key={medusaId} className="last:mb-14">
              <InspirationProductCard
                medusaVariantId={medusaProduct?.variants?.[0].id || ""}
                image={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  product?.data?.images?.[0]?.image?.url ||
                  product?.data?.featuredImage?.url ||
                  ""
                }
                productName={product.data.title || medusaProduct?.title || ""}
                altText={product?.data?.images?.[0]?.image?.alt || product.data.title || ""}
                price={getMedusaProductPrice(medusaProduct)}
                link={`${getPermalink(locale, "product")}/${product.uid}`}
                locale={locale}
                isLoading={isPending}
                onQuantityChange={(quantity) => updateProductQuantity(medusaProduct?.id || medusaId, quantity)}
                productConditioning={resolveProductConditioning(
                  medusaProduct?.metadata?.conditioning,
                  medusaProduct?.metadata?.conditioning_bac
                )}
                productPrice={medusaProduct?.variants?.at(0)?.calculated_price?.calculated_amount || 0}
                isFullImage={product.data.image_full_size_card || false}
                isOutOfStock={medusaProduct?.status === "draft" ? true : false}
                isPriceDynamic={medusaProduct?.metadata?.is_price_dynamic as boolean}
                isRange={medusaProduct?.metadata?.is_surface_based as boolean}
                forSale={isSaleProduct(medusaProduct)}
              />
            </li>
          );
        })}
      </ul>
      <ul className="space-y-3.5 pr-3 sm:hidden">
        {products.map((product) => {
          const medusaId = extractKeyTextValue(product.data.medusaId);
          const medusaProduct = medusaProducts?.[medusaId];

          return (
            <li key={`mobile-${medusaId}`} className="last:mb-16">
              <InspirationProductMobile
                medusaVariantId={medusaProduct?.variants?.[0].id || ""}
                productImage={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  product?.data?.images?.[0]?.image?.url ||
                  product?.data?.featuredImage?.url ||
                  ""
                }
                productName={product.data.title || medusaProduct?.title || ""}
                alt={product?.data?.images?.[0]?.image?.alt || product.data.title || ""}
                productPrice={getMedusaProductPrice(medusaProduct)}
                locale={locale}
                isLoading={isPending}
                onQuantityChange={(quantity) => updateProductQuantity(medusaId, quantity)}
                productConditioning={resolveProductConditioning(
                  medusaProduct?.metadata?.conditioning,
                  medusaProduct?.metadata?.conditioning_bac
                )}
                price={medusaProduct?.variants?.at(0)?.calculated_price?.calculated_amount || 0}
                isFullImage={product.data.image_full_size_card || false}
                isOutOfStock={medusaProduct?.status === "draft" ? true : false}
                isPriceDynamic={medusaProduct?.metadata?.is_price_dynamic as boolean}
                isRange={medusaProduct?.metadata?.is_surface_based as boolean}
                prismicProduct={product}
                medusaProduct={medusaProduct}
              />
            </li>
          );
        })}
      </ul>
      {products && products.length > 0 && (
        <div className="absolute right-0 bottom-0 left-0 z-30">
          {isPending ? (
            <div className="shimmer h-12 rounded-sm" />
          ) : isAllProductsOutOfStock ? (
            <Toast
              id="inspiration-out-of-stock"
              type="warning"
              className="mt-3 max-w-none shadow-none"
              title={__("This pack is out of stock", locale)}
            />
          ) : isProductsInCart ? (
            <div className="bg-product-card flex w-full items-center rounded-sm p-2">
              <div className="flex w-full items-center gap-1 text-sm md:text-base">
                <ShoppingCart size={14} className="text-green-600 md:size-5" aria-hidden="true" />
                <span className="font-medium text-green-600">{__("Pack added to cart !", locale)}</span>
              </div>
              <Link
                href={getPermalink(locale, "cart")}
                aria-label={__("Go to cart", locale)}
                className={cn(
                  buttonVariants(),
                  "flex h-10 items-center justify-center rounded-xs p-0 px-2 text-sm transition-transform duration-150 ease-in-out active:scale-95 md:px-4 md:text-base"
                )}
              >
                {__("View Cart", locale)}
              </Link>
            </div>
          ) : (
            <Button
              onClick={handleAddToCart}
              disabled={isPending || isAddingAllToCart}
              size="md"
              className="font-cabinet-grotesk w-full font-medium disabled:opacity-90"
            >
              {isAddingAllToCart ? (
                <Loader2 className="animate-spin text-white" size={24} aria-hidden="true" />
              ) : (
                <ShoppingCart className="text-white" size={24} aria-hidden="true" />
              )}
              {isAddingAllToCart ? __("Adding to cart...", locale) : __("Order this pack", locale)}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default InspirationProductsList;
